import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import styled, { ThemeProvider } from "styled-components";
import { Highlight, themes } from "prism-react-renderer";
import { space, layout, compose } from "styled-system";
import { themeGet } from "@styled-system/theme-get";
import { css } from "@styled-system/css";

const codeBlockStyles = compose(space, layout);

const CodeBlockWrapper = styled("div")(
  (props) =>
    css({
      position: "relative",
      borderRadius: themeGet("radii.1")(props),
      backgroundColor:
        props.variant === "dark"
          ? themeGet("colors.greyDarkest")(props)
          : themeGet("colors.white")(props),
      border:
        props.variant === "light" ? `1px solid ${themeGet("colors.greyLighter")(props)}` : "none",
      overflow: "hidden"
    }),
  codeBlockStyles
);

const CodeBlockHeader = styled("div")((props) =>
  css({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: themeGet("space.3")(props),
    backgroundColor:
      props.variant === "dark"
        ? themeGet("colors.black")(props)
        : themeGet("colors.greyLightest")(props),
    borderBottom:
      props.variant === "dark"
        ? `1px solid ${themeGet("colors.greyDarker")(props)}`
        : `1px solid ${themeGet("colors.greyLighter")(props)}`
  })
);

const LanguageLabel = styled("span")((props) =>
  css({
    fontSize: themeGet("fontSizes.0")(props),
    fontWeight: themeGet("fontWeights.2")(props),
    color:
      props.variant === "dark"
        ? themeGet("colors.greyLight")(props)
        : themeGet("colors.greyDark")(props),
    textTransform: "uppercase",
    letterSpacing: "0.05em"
  })
);

const CopyButton = styled("button").attrs({
  type: "button"
})((props) =>
  css({
    appearance: "none",
    backgroundColor: "transparent",
    border:
      props.variant === "dark"
        ? `1px solid ${themeGet("colors.grey")(props)}`
        : `1px solid ${themeGet("colors.grey")(props)}`,
    borderRadius: themeGet("radii.1")(props),
    color:
      props.variant === "dark"
        ? themeGet("colors.greyLight")(props)
        : themeGet("colors.greyDark")(props),
    cursor: "pointer",
    fontSize: themeGet("fontSizes.0")(props),
    fontWeight: themeGet("fontWeights.2")(props),
    padding: `${themeGet("space.2")(props)} ${themeGet("space.3")(props)}`,
    transition: themeGet("transition.transitionDefault")(props),
    "&:hover": {
      backgroundColor:
        props.variant === "dark"
          ? themeGet("colors.greyDarker")(props)
          : themeGet("colors.greyLightest")(props),
      borderColor:
        props.variant === "dark"
          ? themeGet("colors.greyLight")(props)
          : themeGet("colors.greyDark")(props),
      color:
        props.variant === "dark"
          ? themeGet("colors.white")(props)
          : themeGet("colors.greyDarkest")(props)
    },
    "&:focus": {
      outline: "none",
      boxShadow: `${themeGet("shadows.thinOutline")(props)} ${themeGet("colors.primary30")(props)}`
    },
    "&:disabled": {
      cursor: "default",
      opacity: 0.6
    }
  })
);

const omitMaxHeightProp = (prop, defaultValidatorFn) => {
  const isValidProp = typeof defaultValidatorFn === "function" ? defaultValidatorFn(prop) : true;
  return prop !== "maxHeight" && isValidProp;
};

const PreTag = styled("pre").withConfig({
  shouldForwardProp: omitMaxHeightProp
})((props) =>
  css({
    margin: 0,
    padding: themeGet("space.r")(props),
    overflow: "auto",
    maxHeight: props.maxHeight || "500px",
    fontSize: themeGet("fontSizes.1")(props),
    lineHeight: "1.5",
    fontFamily: "monospace"
  })
);

const EditableCodeContainer = styled("div").withConfig({
  shouldForwardProp: omitMaxHeightProp
})((props) =>
  css({
    position: "relative",
    maxHeight: props.maxHeight || "500px",
    overflowY: "auto",
    overflowX: "hidden"
  })
);

const CodeTextArea = styled("textarea")((props) =>
  css({
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    minHeight: "100%",
    padding: themeGet("space.r")(props),
    margin: 0,
    border: "none",
    outline: "none",
    resize: "none",
    fontFamily: "monospace",
    fontSize: themeGet("fontSizes.1")(props),
    lineHeight: "1.5",
    backgroundColor: "transparent",
    color: "transparent",
    caretColor:
      props.variant === "dark"
        ? themeGet("colors.white")(props)
        : themeGet("colors.greyDarkest")(props),
    zIndex: 1,
    overflow: "hidden",
    "&::selection": {
      backgroundColor: themeGet("colors.primary30")(props)
    }
  })
);

const HighlightedCode = styled("pre")((props) =>
  css({
    margin: 0,
    padding: themeGet("space.r")(props),
    overflow: "visible",
    fontSize: themeGet("fontSizes.1")(props),
    lineHeight: "1.5",
    fontFamily: "monospace",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    flexShrink: 0
  })
);

const LineNumber = styled("span")((props) =>
  css({
    display: "inline-block",
    width: "2em",
    userSelect: "none",
    opacity: 0.5,
    marginRight: themeGet("space.3")(props),
    textAlign: "right"
  })
);

/**
 * CodeBlock component for displaying syntax-highlighted code with optional features.
 *
 * Supports JSON, JavaScript, TypeScript, Python, SQL, Shell, and more.
 * Includes copy-to-clipboard functionality and optional line numbers.
 * Can be made editable with the `editable` prop.
 */
const CodeBlock = ({
  children,
  language = "json",
  variant = "light",
  showLineNumbers = false,
  showCopyButton = true,
  showHeader = true,
  maxHeight,
  theme,
  editable = false,
  value,
  onChange,
  valid,
  invalid,
  rows = 20,
  ...props
}) => {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef(null);
  const textareaRef = useRef(null);
  const [lineCount, setLineCount] = useState(rows);

  // For editable mode, use value prop; for read-only, use children
  const codeValue = editable
    ? value || ""
    : typeof children === "string"
    ? children.trim()
    : String(children).trim();

  // Update line count for editable mode
  useEffect(() => {
    if (editable && codeValue) {
      const lines = codeValue.split("\n").length;
      setLineCount(Math.max(lines, rows));
    }
  }, [editable, codeValue, rows]);

  // Select prism theme based on variant
  const prismTheme = variant === "dark" ? themes.vsDark : themes.github;

  // Cleanup timeout on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codeValue);
      setCopied(true);
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      // Set new timeout and store reference for cleanup
      timeoutRef.current = setTimeout(() => {
        setCopied(false);
        timeoutRef.current = null;
      }, 2000);
    } catch (err) {
      console.error("Failed to copy code:", err);
    }
  };

  const handleChange = (e) => {
    if (onChange) {
      onChange(e);
    }
  };

  const component = (
    <CodeBlockWrapper variant={variant} {...props}>
      {showHeader && (
        <CodeBlockHeader variant={variant}>
          <LanguageLabel variant={variant}>{language}</LanguageLabel>
          {showCopyButton && !editable && (
            <CopyButton
              variant={variant}
              onClick={handleCopy}
              disabled={copied}
              aria-label={copied ? "Copied!" : "Copy code"}
            >
              {copied ? "Copied!" : "Copy"}
            </CopyButton>
          )}
        </CodeBlockHeader>
      )}
      {editable ? (
        <EditableCodeContainer maxHeight={maxHeight}>
          <div style={{ position: "relative", minHeight: "fit-content" }}>
            <Highlight theme={prismTheme} code={codeValue} language={language}>
              {({ className, style, tokens, getLineProps, getTokenProps }) => (
                <HighlightedCode
                  className={className}
                  style={{
                    ...style,
                    border: invalid
                      ? `1px solid ${themeGet("colors.danger")({ theme })}`
                      : valid
                      ? `1px solid ${themeGet("colors.success")({ theme })}`
                      : "none"
                  }}
                >
                  {tokens.map((line, i) => (
                    <div key={i} {...getLineProps({ line })}>
                      {showLineNumbers && <LineNumber>{i + 1}</LineNumber>}
                      {line.map((token, key) => (
                        <span key={key} {...getTokenProps({ token })} />
                      ))}
                    </div>
                  ))}
                </HighlightedCode>
              )}
            </Highlight>
            <CodeTextArea
              ref={textareaRef}
              variant={variant}
              value={codeValue}
              onChange={handleChange}
              rows={lineCount}
              spellCheck={false}
              style={{
                height: "auto",
                minHeight: "100%"
              }}
            />
          </div>
        </EditableCodeContainer>
      ) : (
        <Highlight theme={prismTheme} code={codeValue} language={language}>
          {({ className, style, tokens, getLineProps, getTokenProps }) => (
            <PreTag className={className} style={style} maxHeight={maxHeight}>
              {/* Using index as key is safe here - tokens array is stable and won't be reordered */}
              {tokens.map((line, i) => (
                <div key={i} {...getLineProps({ line })}>
                  {showLineNumbers && <LineNumber>{i + 1}</LineNumber>}
                  {line.map((token, key) => (
                    <span key={key} {...getTokenProps({ token })} />
                  ))}
                </div>
              ))}
            </PreTag>
          )}
        </Highlight>
      )}
    </CodeBlockWrapper>
  );

  return theme ? <ThemeProvider theme={theme}>{component}</ThemeProvider> : component;
};

CodeBlock.propTypes = {
  /** The code to display. Should be a string. Required when not using editable mode. */
  children: (props, propName, componentName) => {
    if (!props.editable) {
      if (props[propName] === undefined || props[propName] === null) {
        return new Error(
          `Prop \`${propName}\` is required in \`${componentName}\` when \`editable\` is false or not provided.`
        );
      }
      if (typeof props[propName] !== "string") {
        return new Error(
          `Invalid prop \`${propName}\` of type \`${typeof props[
            propName
          ]}\` supplied to \`${componentName}\`, expected \`string\`.`
        );
      }
    } else if (props[propName] !== undefined && typeof props[propName] !== "string") {
      return new Error(
        `Invalid prop \`${propName}\` of type \`${typeof props[
          propName
        ]}\` supplied to \`${componentName}\`, expected \`string\`.`
      );
    }
    return null;
  },
  /** The programming language for syntax highlighting. Defaults to 'json'. Also supports: 'javascript', 'python', 'typescript', 'sql', 'bash', and many more. */
  language: PropTypes.string,
  /** Visual style variant: 'light' (white background, dark text - default) or 'dark' (dark background, light text) */
  variant: PropTypes.oneOf(["light", "dark"]),
  /** Show line numbers on the left side */
  showLineNumbers: PropTypes.bool,
  /** Show the copy button in the header */
  showCopyButton: PropTypes.bool,
  /** Show the header with language label and copy button */
  showHeader: PropTypes.bool,
  /** Maximum height before scrolling (e.g., '300px', '50vh') */
  maxHeight: PropTypes.string,
  /** Specifies the system design theme */
  theme: PropTypes.object,
  /** Enable editable mode. When true, use `value` and `onChange` props instead of `children`. */
  editable: PropTypes.bool,
  /** The value for editable mode. Required when `editable` is true. */
  value: (props, propName, componentName) => {
    if (props.editable && props[propName] !== undefined && typeof props[propName] !== "string") {
      return new Error(
        `Invalid prop \`${propName}\` of type \`${typeof props[
          propName
        ]}\` supplied to \`${componentName}\`, expected \`string\` when \`editable\` is true.`
      );
    }
    return null;
  },
  /** Change handler for editable mode. Required when `editable` is true. Receives the event object. */
  onChange: PropTypes.func,
  /** Validation state - shows success border when true */
  valid: PropTypes.bool,
  /** Validation state - shows error border when true */
  invalid: PropTypes.bool,
  /** Number of rows for editable mode (default: 20) */
  rows: PropTypes.number
  // Also accepts all styled-system space and layout props (m, mt, mb, ml, mr, mx, my, p, pt, pb, pl, pr, px, py, width, height, etc.)
};

export default CodeBlock;
